import { LANGS, type Lang } from "../src/languages";
import { h } from "./dom";
import { LANG, switchLang } from "./i18n";
import { LANGUAGE_NAMES, STRINGS } from "./strings";

/**
 * UI language switcher. `compact` shows "EN"/"ES" (tight headers); otherwise each language
 * is written in itself ("English", "Español") so anyone can find their own.
 */
export function uiLangSelect(compact = false): HTMLSelectElement {
  const select = h(
    "select",
    { class: "select select-lang", "aria-label": STRINGS.uiLanguageLabel, title: STRINGS.uiLanguageLabel },
    ...LANGS.map((lang) =>
      h("option", { value: lang }, compact ? lang.toUpperCase() : LANGUAGE_NAMES[lang]),
    ),
  );
  select.value = LANG;
  select.addEventListener("change", () => switchLang(select.value as Lang));
  return select;
}

export interface WordLangTiles {
  el: HTMLElement;
  /** Currently selected word language. */
  value(): Lang;
}

/**
 * Word-language picker for new rooms: one button-like tile per language, each written in
 * its own language (like a game's language screen). Real radio inputs underneath, so it
 * works with the keyboard (arrows) and screen readers. `labelId` is the visible label's id.
 */
export function wordLangTiles(name: string, labelId: string, value: Lang = LANG): WordLangTiles {
  const inputs = LANGS.map((lang) => {
    const input = h("input", {
      type: "radio",
      name,
      id: `${name}-${lang}`,
      value: lang,
      class: "tile-input visually-hidden",
    });
    input.checked = lang === value;
    return input;
  });
  const el = h(
    "div",
    { class: "lang-tiles", role: "radiogroup", "aria-labelledby": labelId },
    ...LANGS.flatMap((lang, i) => [
      inputs[i]!,
      h("label", { for: `${name}-${lang}`, class: "lang-tile", lang }, LANGUAGE_NAMES[lang]),
    ]),
  );
  return {
    el,
    value: () => {
      const checked = inputs.find((input) => input.checked)?.value;
      return LANGS.find((lang) => lang === checked) ?? value;
    },
  };
}
